/**
 * Legal, consent and health-screening content.
 *
 * Policy bodies live in `legal-documents.ts`. Screening questions and consent
 * items below are structural defaults, editable in Admin → Settings → Screening.
 */

import { placeholder } from './placeholders.js';
import {
  accessibilityStatement,
  cancellationPolicy,
  LEGAL_DOCUMENT_VERSION,
  nutritionDisclaimer,
  privacyPolicy,
  refundPolicy,
  termsAndConditions,
  trainingDisclaimer,
} from './legal-documents.js';

export type ConsentType =
  | 'privacy'
  | 'terms'
  | 'training-disclaimer'
  | 'nutrition-disclaimer'
  | 'cancellation-policy'
  | 'participation'
  | 'photography'
  | 'public-transformation';

export interface ConsentItem {
  type: ConsentType;
  label: string;
  /** Text shown beside the checkbox. */
  statement: string;
  /** Route to the full document, if one exists. */
  documentPath: string | null;
  /** Blocks onboarding completion when true. */
  required: boolean;
  /**
   * Public marketing use requires its own explicit consent; it is never implied
   * by photography consent or by a private progress upload.
   */
  grantsPublicUse: boolean;
}

export const consentItems: readonly ConsentItem[] = [
  {
    type: 'privacy',
    label: 'Privacy Policy',
    statement: 'I have read and accept the Privacy Policy.',
    documentPath: '/privacy',
    required: true,
    grantsPublicUse: false,
  },
  {
    type: 'terms',
    label: 'Terms & Conditions',
    statement: 'I have read and accept the Terms and Conditions.',
    documentPath: '/terms',
    required: true,
    grantsPublicUse: false,
  },
  {
    type: 'training-disclaimer',
    label: 'Training Disclaimer',
    statement: 'I have read and accept the Training Disclaimer.',
    documentPath: '/training-disclaimer',
    required: true,
    grantsPublicUse: false,
  },
  {
    type: 'nutrition-disclaimer',
    label: 'Nutrition Disclaimer',
    statement:
      'I understand that nutrition guidance is general coaching education and is not medical nutrition therapy.',
    documentPath: '/nutrition-disclaimer',
    required: true,
    grantsPublicUse: false,
  },
  {
    type: 'cancellation-policy',
    label: 'Cancellation Policy',
    statement: 'I have read and accept the Cancellation Policy.',
    documentPath: '/cancellation-policy',
    required: true,
    grantsPublicUse: false,
  },
  {
    type: 'participation',
    label: 'Consent to Participate',
    statement:
      'I confirm the health information I have provided is accurate and complete, and I consent to participate in physical training.',
    documentPath: null,
    required: true,
    grantsPublicUse: false,
  },
  {
    type: 'photography',
    label: 'Photograph Consent (optional)',
    statement:
      'I consent to my coach recording private progress photographs for the purpose of tracking my progress. These remain private to me and my coach.',
    documentPath: null,
    required: false,
    grantsPublicUse: false,
  },
  {
    type: 'public-transformation',
    label: 'Public Result Consent (optional)',
    statement:
      'I separately consent to my approved results, imagery or testimonial being used publicly. I understand I may withdraw this consent at any time, after which the public content will be unpublished.',
    documentPath: null,
    required: false,
    grantsPublicUse: true,
  },
];

export type ScreeningAnswerType = 'yes-no' | 'short-text' | 'long-text' | 'number' | 'single-select';

export interface ScreeningQuestion {
  id: string;
  /** Grouping shown as a form section heading. */
  section: 'par-q' | 'medical' | 'injury' | 'lifestyle' | 'nutrition';
  prompt: string;
  type: ScreeningAnswerType;
  required: boolean;
  options?: readonly string[];
  /**
   * When the answer matches this, the onboarding record is flagged for coach
   * review. Flagging is an operational routing signal, never a diagnosis.
   */
  flagWhen?: { equals: string } | { greaterThan: number };
  /** Free-text prompt shown when the question is flagged. */
  followUpPrompt?: string;
}

/**
 * PAR-Q-style screening. Structure is standard self-screening practice; the
 * exact wording the client wishes to use is tracked as `[PAR_Q_CONTENT]` in
 * docs/CONTENT_REQUIRED.md and is editable in the admin portal.
 */
export const screeningQuestions: readonly ScreeningQuestion[] = [
  {
    id: 'parq-heart-condition',
    section: 'par-q',
    prompt:
      'Has a doctor ever said that you have a heart condition, or that you should only do physical activity recommended by a doctor?',
    type: 'yes-no',
    required: true,
    flagWhen: { equals: 'yes' },
    followUpPrompt: 'Please provide any detail you are comfortable sharing.',
  },
  {
    id: 'parq-chest-pain-activity',
    section: 'par-q',
    prompt: 'Do you feel pain in your chest when you do physical activity?',
    type: 'yes-no',
    required: true,
    flagWhen: { equals: 'yes' },
  },
  {
    id: 'parq-chest-pain-rest',
    section: 'par-q',
    prompt: 'In the past month, have you had chest pain when you were not doing physical activity?',
    type: 'yes-no',
    required: true,
    flagWhen: { equals: 'yes' },
  },
  {
    id: 'parq-balance',
    section: 'par-q',
    prompt: 'Do you lose your balance because of dizziness, or do you ever lose consciousness?',
    type: 'yes-no',
    required: true,
    flagWhen: { equals: 'yes' },
  },
  {
    id: 'parq-bone-joint',
    section: 'par-q',
    prompt:
      'Do you have a bone or joint problem that could be made worse by a change in your physical activity?',
    type: 'yes-no',
    required: true,
    flagWhen: { equals: 'yes' },
    followUpPrompt: 'Which area, and what movements aggravate it?',
  },
  {
    id: 'parq-medication',
    section: 'par-q',
    prompt:
      'Are you currently taking prescribed medication for a heart condition or blood pressure?',
    type: 'yes-no',
    required: true,
    flagWhen: { equals: 'yes' },
  },
  {
    id: 'parq-other-reason',
    section: 'par-q',
    prompt: 'Do you know of any other reason why you should not do physical activity?',
    type: 'yes-no',
    required: true,
    flagWhen: { equals: 'yes' },
    followUpPrompt: 'Please describe.',
  },
  {
    id: 'pregnancy',
    section: 'medical',
    prompt: 'Are you currently pregnant or have you given birth in the last 12 months?',
    type: 'single-select',
    required: false,
    options: ['No', 'Yes', 'Prefer not to say'],
    flagWhen: { equals: 'Yes' },
  },
  {
    id: 'medical-conditions',
    section: 'medical',
    prompt:
      'Please list any medical conditions your coach should be aware of. Share only what you are comfortable sharing.',
    type: 'long-text',
    required: false,
  },
  {
    id: 'medications',
    section: 'medical',
    prompt:
      'Are you taking any medication that may affect your training, heart rate or recovery? (Optional)',
    type: 'long-text',
    required: false,
  },
  {
    id: 'current-injuries',
    section: 'injury',
    prompt: 'Do you have any current injuries, pain or physical limitations?',
    type: 'long-text',
    required: false,
  },
  {
    id: 'past-injuries',
    section: 'injury',
    prompt: 'Have you had any significant past injuries or surgery?',
    type: 'long-text',
    required: false,
  },
  {
    id: 'hand-wrist',
    section: 'injury',
    prompt:
      'For boxing: do you have any hand, wrist, shoulder or neck issues we should work around?',
    type: 'long-text',
    required: false,
  },
  {
    // Deliberately not flaggable. Allergies are nutrition information, not a
    // training-risk signal: they belong on the nutrition plan builder and the
    // client's health profile, where the coach acts on them. Routing them through
    // the review flag would fill the review queue with records that need no review
    // and devalue the flag that exists to catch cardiac and joint answers.
    id: 'allergies',
    section: 'nutrition',
    prompt: 'Do you have any food allergies or intolerances?',
    type: 'long-text',
    required: false,
  },
  {
    id: 'dietary-preference',
    section: 'nutrition',
    prompt: 'Do you follow any particular dietary pattern?',
    type: 'single-select',
    required: false,
    options: [
      'No specific pattern',
      'Vegetarian',
      'Vegan',
      'Pescatarian',
      'Halal',
      'Kosher',
      'Gluten free',
      'Dairy free',
      'Other',
    ],
  },
  {
    id: 'sleep-hours',
    section: 'lifestyle',
    prompt: 'On average, how many hours do you sleep per night?',
    type: 'number',
    required: false,
  },
  {
    id: 'activity-level',
    section: 'lifestyle',
    prompt: 'How would you describe your current activity level?',
    type: 'single-select',
    required: true,
    options: [
      'Sedentary - little planned activity',
      'Lightly active - 1-2 sessions per week',
      'Moderately active - 3-4 sessions per week',
      'Very active - 5+ sessions per week',
    ],
  },
];

/**
 * Wording shown when screening answers trigger review. It routes the client to
 * a professional and explicitly avoids interpreting their answers.
 */
export const screeningReviewNotice = {
  heading: 'One or more answers need a quick review before your first session',
  body:
    'Thank you for completing the screening honestly. Based on your answers, your coach will review your responses before your first training session, and medical clearance from your doctor may be required first. This is a standard precaution - it is not an assessment or diagnosis of any condition, and nothing in your answers has been interpreted as one.',
  action: 'Your coach will be in touch to confirm the next step.',
} as const;

export const legal = {
  privacyPolicy,
  termsAndConditions,
  trainingDisclaimer,
  nutritionDisclaimer,
  cancellationPolicy,
  refundPolicy,
  documentVersion: LEGAL_DOCUMENT_VERSION,
  healthScreeningIntro: placeholder('HEALTH SCREENING QUESTIONS'),
  parQContent: placeholder('PAR_Q_CONTENT'),

  consentItems,
  screeningQuestions,
  screeningReviewNotice,

  /**
   * Rendered beneath every nutrition plan and target. Not a placeholder,
   * because omitting it would be worse than a generic-but-accurate statement.
   */
  standingNutritionDisclaimer:
    'Nutrition targets and guidance are provided as general coaching education by a fitness professional and are individualised to your stated goals, preferences and training. They are not medical nutrition therapy, do not diagnose or treat any condition, and do not replace advice from your doctor or an accredited practising dietitian. Stop and seek medical advice if you feel unwell.',

  standingTrainingDisclaimer:
    'Physical training carries inherent risk. Follow the technique guidance provided, work within your current capability, and stop any exercise that causes pain. This program is based on the information you have given your coach - tell your coach immediately if your health, medication or injury status changes.',

  /** Statement shown on the accessibility route. */
  accessibilityStatement,

  /**
   * Technical controls the software implements. These documents are the
   * operative policies of the business; they are not a certification against
   * a named statute.
   */
  complianceNotice:
    'These documents are the operative policies of Iron Resolve Combat Fitness. The platform implements role-based access control, ownership checks, private storage with signed access, consent recording and audit logging. They have not been certified against a named privacy statute. Obtain independent legal, privacy and professional-scope review if you need confirmation for your own circumstances.',
} as const;

/** Consents that must be recorded before onboarding can complete. */
export const requiredConsentTypes: readonly ConsentType[] = consentItems
  .filter((item) => item.required)
  .map((item) => item.type);

/** Screening questions whose answers can raise a review flag. */
export const flaggableScreeningQuestions: readonly ScreeningQuestion[] =
  screeningQuestions.filter((question) => question.flagWhen !== undefined);
